import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trash2, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { localDateTimeStamp } from '@/lib/dateRange';

/**
 * Canonical waste reasons — stored into the existing stock_usage.used_by
 * field (no schema change). The wastage report recognizes these values.
 */
export const WASTE_REASONS = [
  { value: 'spoiled', label: 'Spoiled' },
  { value: 'expired', label: 'Expired' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'over_production', label: 'Over Production' },
  { value: 'preparation_waste', label: 'Preparation Waste' },
  { value: 'other', label: 'Other' },
];

export const reasonLabel = (v) =>
  WASTE_REASONS.find((r) => r.value === String(v || '').toLowerCase())?.label ||
  (v ? String(v).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '—');

/**
 * One shared Record Waste dialog, reused from Stock and Recipes production
 * history. Records normal stock-OUT via POST /stock/usage so inventory stays
 * one source of truth.
 */
export function RecordWasteDialog({
  open,
  onOpenChange,
  items = [],
  branches = [],
  defaultBranchId = '',
  defaultItemId = '',
  lockItem = false,
  onRecorded,
}) {
  const [itemId, setItemId] = useState('');
  const [branchId, setBranchId] = useState('');
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState('spoiled');
  const [note, setNote] = useState('');
  const [itemSearch, setItemSearch] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setItemId(defaultItemId || '');
      setBranchId(defaultBranchId || '');
      setQty('');
      setReason('spoiled');
      setNote('');
      setItemSearch('');
    }
  }, [open, defaultItemId, defaultBranchId]);

  const selectedItem = useMemo(() => items.find((i) => i.id === itemId), [items, itemId]);
  const unit = selectedItem?.unit || 'unit';
  const cost = selectedItem?.cost_price || 0;
  const estValue = (parseFloat(qty) || 0) * cost;

  const filteredItems = useMemo(() => {
    const q = itemSearch.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => (i.name || '').toLowerCase().includes(q));
  }, [items, itemSearch]);

  const submit = async () => {
    if (!itemId) { toast.error('Select an item'); return; }
    const q = parseFloat(qty);
    if (!q || q <= 0) { toast.error('Enter a quantity greater than 0'); return; }
    setSaving(true);
    try {
      await api.post('/stock/usage', {
        item_id: itemId,
        branch_id: branchId || undefined,
        quantity: q,
        used_by: reason,
        notes: reason === 'other' ? (note || '').trim() : '',
        date: localDateTimeStamp(),
      });
      toast.success('Waste recorded — stock reduced');
      onOpenChange(false);
      onRecorded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to record waste');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="record-waste-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash2 size={18} className="text-error" /> Record Waste
          </DialogTitle>
          <DialogDescription className="text-xs">
            Reduces inventory via a normal stock-out. Reason, branch, date and user are saved automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          {/* Item */}
          <div className="space-y-1.5">
            <Label className="text-xs">Item</Label>
            {lockItem && selectedItem ? (
              <div className="h-10 flex items-center px-3 rounded-md border border-border bg-muted/40 text-sm" data-testid="waste-item-locked">
                {selectedItem.name}
              </div>
            ) : (
              <Select value={itemId} onValueChange={setItemId}>
                <SelectTrigger data-testid="waste-item-select"><SelectValue placeholder="Select item / ingredient / product" /></SelectTrigger>
                <SelectContent>
                  <div className="p-2">
                    <Input
                      value={itemSearch}
                      onChange={(e) => setItemSearch(e.target.value)}
                      placeholder="Search..."
                      className="h-8 text-xs"
                      data-testid="waste-item-search"
                      onKeyDown={(e) => e.stopPropagation()}
                    />
                  </div>
                  {filteredItems.slice(0, 200).map((i) => (
                    <SelectItem key={i.id} value={i.id} data-testid={`waste-item-opt-${i.id}`}>
                      {i.name} <span className="text-muted-foreground">· {i.unit}</span>
                    </SelectItem>
                  ))}
                  {filteredItems.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">No items</div>}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Qty + Branch */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Quantity ({unit})</Label>
              <Input
                type="number" min="0" step="any" value={qty}
                onChange={(e) => setQty(e.target.value)}
                placeholder="0" data-testid="waste-qty-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Branch</Label>
              <Select value={branchId || 'none'} onValueChange={(v) => setBranchId(v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="waste-branch-select"><SelectValue placeholder="Branch" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No branch</SelectItem>
                  {branches.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Reason */}
          <div className="space-y-1.5">
            <Label className="text-xs">Reason</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="waste-reason-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {WASTE_REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value} data-testid={`waste-reason-${r.value}`}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {reason === 'other' && (
            <div className="space-y-1.5">
              <Label className="text-xs">Note</Label>
              <Textarea
                value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Short explanation" rows={2} data-testid="waste-note-input"
              />
            </div>
          )}

          {selectedItem && (parseFloat(qty) || 0) > 0 && (
            <div className="text-xs text-muted-foreground" data-testid="waste-est-value">
              Estimated waste value: <span className="font-semibold text-error">{estValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="waste-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-error hover:bg-error/90 text-white" data-testid="waste-submit-btn">
            {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Trash2 size={14} className="mr-1" />} Record Waste
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default RecordWasteDialog;
